import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faRotate } from '@fortawesome/pro-solid-svg-icons';

const schema = yup.object().shape({
	pullDate: yup.string().nullable()
});

const SynchronizeProductModal = NiceModal.create(({ credentialsID, storeName }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { t } = useTranslation();
	const formMethods = useForm({
		defaultValues: {
			pullDate: new Date().toISOString()
		},
		resolver: yupResolver(schema)
	});

	const {
		setValue,
		handleSubmit,
		formState: { isDirty, isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isSubmitting || isFormLoading;

	const onSubmit = async (data) => {
		try {
			await axiosPrivate.post(
				credentialsID ? PRODUCTS.PRODUCT_SYNC_BY_SALES_CHANNEL_ID : PRODUCTS.PRODUCT_SYNC_ALL_CHANNELS,
				{
					pullDate: data?.pullDate,
					...(credentialsID && { salesChannelCredentialsID: credentialsID })
				}
			);

			showSuccess({
				header: 'syncProductsSuccess.title',
				message: t('syncProductsSuccess.message', {
					storeName
				})
			});
			modal.remove();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal maxWidth="small" fullwidth className="modal-synchronizeOrder">
			<ModalTitle>
				<Typography variant="p1">syncProducts</Typography>
			</ModalTitle>
			<ModalContent>
				<Alert content="syncProductsInfo" className="my-3" showIcon />
				<Form formMethods={formMethods}>
					<FormItem label="startDate" toolTip="syncProductsDateDescription" required>
						<DateInput
							name="pullDate"
							setValue={(date) =>
								setValue('pullDate', date ?? null, { shouldDirty: true, shouldValidate: true })
							}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" onClick={() => modal.remove()} variant="outline" />
				<Button
					onClick={handleSubmit(onSubmit, AppLogger.log)}
					disabled={!isDirty || isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faRotate}
					label="synchronize"
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default SynchronizeProductModal;
